import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { GoogleMap, DirectionsRenderer, Polyline, Marker } from '@react-google-maps/api';
import type { City, Segment, GoogleMapsLoadedProps } from '../../types';

export type { City, Segment };

interface CityLeg {
  from: string;
  to: string;
  distance: string;
  duration: string;
  failed?: boolean;
}

interface SegmentRoute {
  fromCity: City;
  toCity: City;
  directions: google.maps.DirectionsResult | null;
  failed: boolean;
}

export interface RouteData {
  routeString: string;
  totalDistance: string | null;
  totalDuration: string | null;
  cityLegs: CityLeg[];
}

interface TripMapProps extends GoogleMapsLoadedProps {
  cities: City[];
  segments: Segment[];
  onRouteDataReady?: (data: RouteData) => void;
}

function TripMap({ cities, segments, isLoaded, loadError, onRouteDataReady }: TripMapProps) {
  const [directionsResponse, setDirectionsResponse] = useState<google.maps.DirectionsResult | null>(null);
  const [segmentRoutes, setSegmentRoutes] = useState<SegmentRoute[]>([]);
  const [routeDistance, setRouteDistance] = useState<string | null>(null);
  const [routeDuration, setRouteDuration] = useState<string | null>(null);
  const [directionsError, setDirectionsError] = useState<string | null>(null);
  const [cityToCityLegs, setCityToCityLegs] = useState<CityLeg[]>([]);
  const [useSegmentBySegment, setUseSegmentBySegment] = useState(false);
  const [routeString, setRouteString] = useState<string>('');

  useEffect(() => {
    if (!isLoaded || !cities || cities.length < 2 || !segments) return;

    // Order cities based on segment sequence (chronological order from trip data)
    const orderedCityIds: number[] = [];
    const sortedSegments = [...segments].sort((a, b) => {
      const dayDiff = (a.day || 0) - (b.day || 0);
      if (dayDiff !== 0) return dayDiff;
      return (a.position || 0) - (b.position || 0);
    });

    // Extract unique city IDs in the order they appear in segments
    sortedSegments.forEach(segment => {
      if (segment.city_id && !orderedCityIds.includes(segment.city_id)) {
        orderedCityIds.push(segment.city_id);
      }
    });

    // Map city IDs to actual city objects with coordinates
    const orderedCities = orderedCityIds
      .map(cityId => cities.find(c => c.id === cityId))
      .filter((city): city is City => !!city && !!city.latitude && !!city.longitude);

    if (orderedCities.length < 2) return;

    // Set the route string for display
    setRouteString(orderedCities.map((city, index) => `${index + 1}. ${city.name}`).join(' → '));

    const directionsService = new window.google.maps.DirectionsService();

    // First, try full route calculation
    const origin = { lat: orderedCities[0].latitude, lng: orderedCities[0].longitude };
    const destination = {
      lat: orderedCities[orderedCities.length - 1].latitude,
      lng: orderedCities[orderedCities.length - 1].longitude
    };

    const intermediateCities = orderedCities.slice(1, -1);
    const maxWaypoints = 23;

    let waypoints = intermediateCities.map(city => ({
      location: { lat: city.latitude, lng: city.longitude },
      stopover: true
    }));

    if (waypoints.length > maxWaypoints) {
      console.warn(`Too many cities (${waypoints.length}). Limiting to ${maxWaypoints} waypoints.`);
      waypoints = waypoints.slice(0, maxWaypoints);
    }

    directionsService.route(
      {
        origin: origin,
        destination: destination,
        waypoints: waypoints,
        optimizeWaypoints: false,
        travelMode: window.google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: false,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK && result) {
          // Full route succeeded
          setDirectionsResponse(result);
          setDirectionsError(null);
          setUseSegmentBySegment(false);

          let totalDistance = 0;
          let totalDuration = 0;

          const legs = result.routes[0].legs.map((leg, index) => {
            totalDistance += leg.distance?.value || 0;
            totalDuration += leg.duration?.value || 0;

            return {
              from: orderedCities[index].name,
              to: orderedCities[index + 1].name,
              distance: ((leg.distance?.value || 0) / 1000).toFixed(1),
              duration: ((leg.duration?.value || 0) / 3600).toFixed(1)
            };
          });

          setCityToCityLegs(legs);
          setRouteDistance((totalDistance / 1000).toFixed(1));
          setRouteDuration((totalDuration / 3600).toFixed(1));
        } else {
          // Full route failed - try segment by segment
          console.warn('Full route failed, trying segment by segment...');
          setUseSegmentBySegment(true);
          calculateSegmentBySegment(orderedCities, directionsService);
        }
      }
    );

    const calculateSegmentBySegment = async (orderedCities: City[], directionsService: google.maps.DirectionsService) => {
      const routes: SegmentRoute[] = [];
      const legs: CityLeg[] = [];
      let totalDistance = 0;
      let totalDuration = 0;
      let hasFailedSegments = false;

      // Calculate each segment sequentially
      for (let i = 0; i < orderedCities.length - 1; i++) {
        const fromCity = orderedCities[i];
        const toCity = orderedCities[i + 1];

        try {
          const result = await new Promise<google.maps.DirectionsResult | null>((resolve) => {
            directionsService.route(
              {
                origin: { lat: fromCity.latitude, lng: fromCity.longitude },
                destination: { lat: toCity.latitude, lng: toCity.longitude },
                travelMode: window.google.maps.TravelMode.DRIVING,
              },
              (result, status) => {
                if (status === window.google.maps.DirectionsStatus.OK && result) {
                  resolve(result);
                } else {
                  resolve(null);
                }
              }
            );
          });

          if (result) {
            const leg = result.routes[0].legs[0];
            const distance = leg.distance?.value || 0;
            const duration = leg.duration?.value || 0;
            totalDistance += distance;
            totalDuration += duration;

            routes.push({ fromCity, toCity, directions: result, failed: false });
            legs.push({
              from: fromCity.name,
              to: toCity.name,
              distance: (distance / 1000).toFixed(1),
              duration: (duration / 3600).toFixed(1),
              failed: false
            });
          } else {
            hasFailedSegments = true;
            routes.push({ fromCity, toCity, directions: null, failed: true });
            legs.push({
              from: fromCity.name,
              to: toCity.name,
              distance: '-',
              duration: '-',
              failed: true
            });
          }
        } catch (error) {
          hasFailedSegments = true;
          routes.push({ fromCity, toCity, directions: null, failed: true });
          legs.push({
            from: fromCity.name,
            to: toCity.name,
            distance: '-',
            duration: '-',
            failed: true
          });
        }
      }

      setSegmentRoutes(routes);
      setCityToCityLegs(legs);

      if (totalDistance > 0) {
        setRouteDistance((totalDistance / 1000).toFixed(1));
        setRouteDuration((totalDuration / 3600).toFixed(1));
      }

      if (hasFailedSegments) {
        setDirectionsError('Some segments cannot be driven. Dashed lines indicate non-drivable routes.');
      }
    };
  }, [isLoaded, cities, segments]);

  // Call onRouteDataReady when route data is available
  useEffect(() => {
    if (onRouteDataReady && routeString) {
      onRouteDataReady({
        routeString,
        totalDistance: routeDistance,
        totalDuration: routeDuration,
        cityLegs: cityToCityLegs,
      });
    }
  }, [onRouteDataReady, routeString, routeDistance, routeDuration, cityToCityLegs]);

  if (loadError) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Error loading Google Maps: {loadError.message}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!isLoaded) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!cities || cities.length === 0) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            No city data available for map display
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Order cities based on segment sequence (chronological order from trip data)
  const orderedCityIds: number[] = [];
  if (segments) {
    const sortedSegments = [...segments].sort((a, b) => {
      const dayDiff = (a.day || 0) - (b.day || 0);
      if (dayDiff !== 0) return dayDiff;
      return (a.position || 0) - (b.position || 0);
    });

    // Extract unique city IDs in the order they appear in segments
    sortedSegments.forEach(segment => {
      if (segment.city_id && !orderedCityIds.includes(segment.city_id)) {
        orderedCityIds.push(segment.city_id);
      }
    });
  }

  // Map city IDs to actual city objects with coordinates
  const orderedCities = orderedCityIds
    .map(cityId => cities.find(c => c.id === cityId))
    .filter((city): city is City => !!city && !!city.latitude && !!city.longitude);

  if (orderedCities.length === 0) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Cities found but no coordinates available for map display
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Calculate bounds to fit all cities
  const bounds = {
    north: Math.max(...orderedCities.map(city => city.latitude)),
    south: Math.min(...orderedCities.map(city => city.latitude)),
    east: Math.max(...orderedCities.map(city => city.longitude)),
    west: Math.min(...orderedCities.map(city => city.longitude))
  };

  const center = {
    lat: (bounds.north + bounds.south) / 2,
    lng: (bounds.east + bounds.west) / 2
  };

  const latDiff = bounds.north - bounds.south;
  const lngDiff = bounds.east - bounds.west;
  const maxDiff = Math.max(latDiff, lngDiff);

  let zoom = 8;
  if (maxDiff > 10) zoom = 4;
  else if (maxDiff > 5) zoom = 5;
  else if (maxDiff > 2) zoom = 6;
  else if (maxDiff > 1) zoom = 7;
  else zoom = 8;

  // Dashed line symbol for failed segments
  const dashedLineSymbol = {
    path: 'M 0,-1 0,1',
    strokeOpacity: 1,
    strokeWeight: 3,
    scale: 3,
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        {directionsError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {directionsError}
          </Alert>
        )}
        <Box sx={{ height: { xs: 250, sm: 350, md: 400 }, width: '100%', borderRadius: 2, overflow: 'hidden' }}>
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={center}
            zoom={zoom}
            options={{
              zoomControl: true,
              streetViewControl: false,
              mapTypeControl: true,
              fullscreenControl: true,
            }}
          >
            {/* Full route - when it succeeds */}
            {!useSegmentBySegment && directionsResponse && (
              <DirectionsRenderer
                directions={directionsResponse}
                options={{
                  suppressMarkers: false,
                  polylineOptions: {
                    strokeColor: '#1976d2',
                    strokeOpacity: 0.8,
                    strokeWeight: 5,
                  },
                }}
              />
            )}

            {/* Segment by segment - when full route fails */}
            {useSegmentBySegment && segmentRoutes.map((route, index) => (
              <React.Fragment key={index}>
                {route.directions ? (
                  <DirectionsRenderer
                    directions={route.directions}
                    options={{
                      suppressMarkers: true,
                      polylineOptions: {
                        strokeColor: '#1976d2',
                        strokeOpacity: 0.8,
                        strokeWeight: 5,
                      },
                    }}
                  />
                ) : (
                  <Polyline
                    path={[
                      { lat: route.fromCity.latitude, lng: route.fromCity.longitude },
                      { lat: route.toCity.latitude, lng: route.toCity.longitude }
                    ]}
                    options={{
                      strokeColor: '#1976d2',
                      strokeOpacity: 0,
                      icons: [{
                        icon: dashedLineSymbol,
                        offset: '0',
                        repeat: '15px'
                      }],
                      geodesic: true,
                    }}
                  />
                )}
              </React.Fragment>
            ))}

            {/* Show markers when using segment by segment */}
            {useSegmentBySegment && orderedCities.map((city, index) => (
              <Marker
                key={city.id}
                position={{ lat: city.latitude, lng: city.longitude }}
                label={{
                  text: String.fromCharCode(65 + index), // A, B, C, etc.
                  color: 'white',
                  fontWeight: 'bold',
                }}
                title={city.name}
              />
            ))}
          </GoogleMap>
        </Box>
      </CardContent>
    </Card>
  );
}

export default TripMap;
